import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import type { HourOfDayStats } from '@tracearr/shared';
import { getHour12 } from '@/lib/timeFormat';
import { ChartSkeleton } from '@/components/ui/skeleton';
import { ChartEmpty } from './ChartEmpty';
import { buildMediaGroupSeries, stackedTooltipRows } from './chartUtils';

interface HourOfDayChartProps {
  /** Rows carrying a mediaType stack movies, shows and music per hour. */
  data: HourOfDayStats[] | undefined;
  isLoading?: boolean;
  height?: number;
}

export function HourOfDayChart({ data, isLoading, height = 250 }: HourOfDayChartProps) {
  const { t } = useTranslation(['common', 'pages']);

  const options = useMemo<Highcharts.Options | null>(() => {
    if (!data || data.length === 0) {
      return {};
    }

    const hours = [...new Set(data.map((d) => d.hour))];
    const hourIndex = new Map(hours.map((hour, index) => [hour, index]));
    const stacked = data.some((d) => d.mediaType !== undefined);
    const groups = stacked
      ? buildMediaGroupSeries(data, (d) => hourIndex.get(d.hour) ?? d.hour)
      : [];
    if (stacked && groups.length === 0) return null;

    // Format hour labels based on time format preference
    const formatHour = (hour: number): string => {
      if (!getHour12()) {
        return `${hour.toString().padStart(2, '0')}:00`;
      }
      if (hour === 0) return '12am';
      if (hour === 12) return '12pm';
      return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
    };

    return {
      chart: {
        type: 'column',
        height,
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
        reflow: true,
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: groups.length >= 2,
        itemStyle: {
          color: 'hsl(var(--muted-foreground))',
        },
        itemHoverStyle: {
          color: 'hsl(var(--foreground))',
        },
      },
      xAxis: {
        categories: hours.map(formatHour),
        labels: {
          style: {
            color: 'hsl(var(--muted-foreground))',
            fontSize: '10px',
          },
          rotation: -45,
        },
        lineColor: 'hsl(var(--border))',
        tickColor: 'hsl(var(--border))',
      },
      yAxis: {
        title: {
          text: undefined,
        },
        labels: {
          style: {
            color: 'hsl(var(--muted-foreground))',
          },
        },
        gridLineColor: 'hsl(var(--border))',
        min: 0,
      },
      plotOptions: {
        column: stacked
          ? { borderRadius: 2, pointPadding: 0.1, groupPadding: 0.1, stacking: 'normal' }
          : {
              borderRadius: 2,
              color: 'hsl(var(--chart-2))',
              pointPadding: 0.1,
              groupPadding: 0.1,
              states: {
                hover: {
                  color: 'hsl(var(--chart-2) / 0.8)',
                },
              },
            },
      },
      tooltip: {
        backgroundColor: 'hsl(var(--popover))',
        borderColor: 'hsl(var(--border))',
        style: {
          color: 'hsl(var(--popover-foreground))',
        },
        shared: stacked,
        formatter: function () {
          if (stacked) {
            const rows = stackedTooltipRows(this.points ?? [], t('pages:history.totalPlays'));
            return `<b>${this.category}</b><br/>${rows}`;
          }
          return `<b>${this.category}</b><br/>${t('labels.plays')}: ${this.y}`;
        },
      },
      series: stacked
        ? groups.map((group): Highcharts.SeriesColumnOptions => ({
            type: 'column',
            name: t(group.labelKey),
            color: group.color,
            stacking: 'normal',
            data: group.data,
          }))
        : [
            {
              type: 'column',
              name: t('labels.plays'),
              data: data.map((d) => d.count),
            },
          ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 400,
            },
            chartOptions: {
              xAxis: {
                labels: {
                  style: {
                    fontSize: '8px',
                  },
                  rotation: -60,
                },
              },
            },
          },
        ],
      },
    };
  }, [data, height, t]);

  if (isLoading) {
    return <ChartSkeleton height={height} />;
  }

  if (!data || data.length === 0 || !options) {
    return <ChartEmpty height={height} message={t('empty.noData')} />;
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { width: '100%', height: '100%' } }}
    />
  );
}
